import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/* ============================================================================
   VIETNAMESE KEYWORD BRIDGE

   THE PROBLEM, MEASURED ON THIS STORE (pushpush.vn, 2026-07-26)
   Every product is titled in English, and Shopify's storefront search only looks at
   title / product_type / vendor / variants.title — never the description. Measured:
   "ao thun" (with tone marks) -> 0 results, "ao khoac" -> 0, "chan vay" -> 0, "vay" -> 0,
   while "tee OR t-shirt" -> 22 products. Adding `options[fields]=...,body` changes nothing
   (measured: still 0), so the description being Vietnamese does not help. A shopper typing
   their own language is told the shop has nothing, on a shop with 22 tees.

   WHAT THIS DOES
   Rewrites the query before it is sent, using a merchant-editable table
   (Theme settings > Search > Vietnamese search keywords -> settings.pp_search_synonyms,
   handed to the element as data-pp-synonyms). "ao thun" becomes
   `"ao thun" OR tee OR t-shirt OR tshirt`.

   WHY THE SHOPPER'S OWN PHRASE STAYS IN, QUOTED
   Measured, all three forms, same store, same minute:
     tee OR t-shirt OR tshirt                 -> 22
     ao thun OR tee OR t-shirt                -> 18   (unquoted: the two words fall apart
                                                       and bind to the neighbouring OR)
     "ao thun" OR tee OR t-shirt OR tshirt    -> 23   <- best
   The quoted phrase is one term, so it adds the product whose own text carries the Vietnamese
   words instead of subtracting. It also means the day the shop puts Vietnamese tags on its
   products — the real fix, which lives in the admin, not here — those tags start counting
   immediately with no theme change. And it keeps the shopper's words at the front of the query,
   which is what the results page echoes back at them.

   WHY WORDS ARE MATCHED WITHOUT TONE MARKS
   Both sides are stripped to bare ASCII before comparing, so one table row covers every way the
   same phrase gets typed: with tone marks, without them, upper case or lower. Half of Vietnamese
   shoppers type without tone marks; a table keyed on the accented spelling would miss them. It is
   also what lets the table itself stay ASCII, which is what tools/check.js requires of every file
   outside locales/.

   WHY UNMATCHED WORDS ARE LEFT ALONE
   Measured: "tee OR t-shirt trang" -> 8, i.e. a leftover word still narrows the result set the
   way the shopper meant. Colour words already work on this store because they are variant option
   values, and variants.title IS searched. So only the words we have a mapping for are touched.
   ============================================================================ */

/**
 * Reduces a word to bare lowercase ASCII so the table can be keyed on unaccented spellings.
 * `d` with stroke is handled first: it is a distinct letter, not a letter plus a mark, so NFD
 * leaves it alone and it would survive the combining-mark strip.
 * Search operators are dropped too — "thun*" from a half-typed query must still match "thun".
 * @param {string} word
 * @returns {string}
 */
function normaliseTerm(word) {
  return word
    .toLowerCase()
    .replace(/\u0111/g, 'd')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/["*]/g, '');
}

/** Parsed tables, keyed by the raw setting text. @type {Map<string, {words: string[], synonyms: string[]}[]>} */
const synonymTables = new Map();

/**
 * Parses the merchant's table. One rule per line, `vietnamese phrase = english, english, ...`.
 * Anything unparseable is skipped rather than thrown: a typo in a theme setting must not take
 * the search box down with it.
 * @param {string} source - raw setting text
 * @returns {{words: string[], synonyms: string[]}[]} longest phrases first
 */
function parseSynonymTable(source) {
  const cached = synonymTables.get(source);
  if (cached) return cached;

  const rules = [];
  for (const line of source.split('\n')) {
    const split = line.indexOf('=');
    if (split < 0) continue;

    const words = line.slice(0, split).trim().split(/\s+/).map(normaliseTerm).filter(Boolean);
    /* A synonym of two or more words is quoted here, once, at parse time. Unquoted it stops being
       one alternative and falls apart across the OR chain: `... OR tank top` asks Shopify for
       "tank, or tank, and also top" rather than for the phrase, and the result set changes shape
       for reasons no one reading the table would predict. */
    const synonyms = line
      .slice(split + 1)
      .split(',')
      .map((word) => word.trim().replace(/"/g, ''))
      .filter(Boolean)
      .map((word) => (/\s/.test(word) ? `"${word}"` : word));

    if (words.length && synonyms.length) rules.push({ words, synonyms });
  }

  /* Longest phrase wins: the table holds both "ao" -> top and "ao khoac" -> jacket, and a shopper
     asking for a jacket must not be served the whole tops wall because the shorter rule matched
     the first word. */
  rules.sort((a, b) => b.words.length - a.words.length);

  synonymTables.set(source, rules);
  return rules;
}

/**
 * Expands the Vietnamese phrases in a query into the English words the catalogue actually uses.
 * Returns the query untouched when there is no table, no match, or when the shopper has written
 * their own search operators.
 * @param {string} query - exactly what the shopper typed
 * @param {string} source - raw setting text (data-pp-synonyms)
 * @returns {string}
 */
function expandQuery(query, source) {
  if (!source) return query;

  /* Hands off a query that already carries operators. A quote means the shopper asked for an
     exact phrase and wrapping it again would corrupt it; an ` OR ` means this query has already
     been through here (the results page re-submits what is in its box) and expanding twice would
     nest quotes inside quotes. Same principle as the wildcard rule below: an operator the shopper
     can see is theirs, not ours. */
  if (query.includes('"') || / OR /.test(query)) return query;

  const rules = parseSynonymTable(source);
  if (!rules.length) return query;

  const typed = query.trim().split(/\s+/).filter(Boolean);
  if (!typed.length) return query;
  const bare = typed.map(normaliseTerm);

  const out = [];
  let cursor = 0;
  let matched = false;

  while (cursor < typed.length) {
    const rule = rules.find(
      ({ words }) =>
        cursor + words.length <= typed.length && words.every((word, step) => bare[cursor + step] === word)
    );

    if (!rule) {
      out.push(typed[cursor]);
      cursor += 1;
      continue;
    }

    /* The shopper's own spelling is what gets quoted, tone marks and all — the table key is only
       ever used for matching. Operators are stripped from it though: a `*` inside a quoted phrase
       is a literal asterisk to Shopify, so a stray one would turn the phrase into a term that can
       never match. In practice nothing reaches here carrying one — both boxes are pre-filled from
       a query the wildcard has been stripped out of — this is the belt to that braces. */
    const phrase = typed
      .slice(cursor, cursor + rule.words.length)
      .join(' ')
      .replace(/["*]/g, '');
    out.push([`"${phrase}"`, ...rule.synonyms].join(' OR '));
    cursor += rule.words.length;
    matched = true;
  }

  return matched ? out.join(' ') : query;
}

/**
 * The exact inverse of expandQuery: turns `"ao thun" OR tee OR t-shirt OR tshirt trang` back into
 * `ao thun trang`. Used to fill the search boxes on the results page, so what a shopper sees is
 * what they typed and refining from there starts from their words rather than from ours.
 *
 * WHY THIS EXISTS ON TOP OF THE LIQUID EQUIVALENT
 * The section already prints a cleaned query server-side, but Liquid has no regular expressions:
 * all it can do is take the text between the first pair of quotes, which silently drops anything
 * the shopper typed AFTER the phrase we matched. Searching "ao thun trang" would leave "ao thun"
 * in the box, and a shopper refining from there would lose their colour without being told. Here
 * the table is in hand, so each synonym chain can be matched exactly and removed exactly.
 *
 * Every rule emits a known, fixed tail — ` OR ` plus its own synonyms — so the match is a literal
 * string comparison, not a guess. Anything that does not carry a tail we recognise is left alone.
 * @param {string} query - the query as run, straight from the URL
 * @param {string} source - raw setting text (data-pp-synonyms)
 * @returns {string}
 */
function collapseQuery(query, source) {
  let out = query;

  for (const { synonyms } of parseSynonymTable(source)) {
    const tail = ` OR ${synonyms.join(' OR ')}`;

    /* Walk every quoted phrase and check whether this rule's tail follows it. Scanning by index
       rather than by splitting keeps quoted phrases containing spaces in one piece. */
    for (let open = out.indexOf('"'); open !== -1; open = out.indexOf('"', open + 1)) {
      const close = out.indexOf('"', open + 1);
      if (close === -1) break;
      if (!out.startsWith(tail, close + 1)) continue;

      out = out.slice(0, open) + out.slice(open + 1, close) + out.slice(close + 1 + tail.length);
      open = -1; // restart: the string just shifted underneath us
    }
  }

  return out;
}

/**
 * Puts the shopper's own words back into a search box that a search page pre-filled with the
 * query as run. Reads the URL rather than the field, because the field has already been through
 * Liquid's rougher version of the same clean-up and the words it dropped are only in the URL.
 * @param {HTMLInputElement | null | undefined} input
 * @param {string} source - raw setting text (data-pp-synonyms)
 */
function restoreShopperWords(input, source) {
  if (!input || !source) return;

  const current = new URL(location.href).searchParams.get('q');
  if (!current) return;

  /* The wildcard is machinery too and the box must never show it — same rule the rest of this
     file follows. It only ever sits on the final word. */
  const collapsed = collapseQuery(current, source).replace(/\*$/, '');
  if (collapsed && collapsed !== current) input.value = collapsed;
}

/**
 * The same expansion for the search box that sits on the results page
 * (sections/pp-collection.liquid). That form is NOT inside a predictive-search-component, and the
 * element that owns it — assets/pp-collection.js — is a different file with a different job, so
 * this wrapper carries the behaviour instead of forking the table into a second script.
 *
 * It listens on itself, i.e. one hop above the form, so it runs BEFORE the wildcard rule bound
 * higher up on <pp-collection>: the query has to be expanded first and get its trailing `*`
 * second. Verified on the store that the two compose — `"ao thun" OR tee OR t-shirt OR tshirt*`
 * returns the same 23 products as the same query without the wildcard.
 */
class PpSearchExpand extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
    restoreShopperWords(this.querySelector('input[name="q"]'), this.getAttribute('data-pp-synonyms') || '');
    this.#rescueEmptyResults();
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
  }

  /**
   * Rewrites the field itself rather than intercepting the navigation, so this stays an ordinary
   * GET form: with the script blocked or broken the box still searches, exactly as it does today.
   * @param {Event} event
   */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const input = form.querySelector('input[name="q"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.value = expandQuery(input.value, this.getAttribute('data-pp-synonyms') || '');
  };

  /**
   * Covers the queries that never went through a search box: a link a shopper was sent, a browser
   * autocomplete of a search made before this feature existed, a typed URL. Liquid cannot re-run a
   * search, so a page that has already come back with nothing is the only place left to catch it.
   *
   * Deliberately narrow, because this navigates on its own:
   *   - the page must report EXACTLY zero results (data-pp-results, from search.results_count);
   *     a missing or unreadable attribute means no
   *   - the query must expand to something different, which only a table hit can do
   *
   * IT READS THE URL, NOT THE SEARCH BOX, and that distinction is the whole safety argument.
   * The box is deliberately cleaned for display — it holds the shopper's phrase with the
   * machinery stripped out. Feeding that back through here caused two faults, both seen on the
   * store before this was changed: searching "ao ba lo zzzz" silently DROPPED the "zzzz", because
   * the box only ever held "ao ba lo"; and any expanded query that still found nothing would
   * bounce forever, since the cleaned box re-expanded to the same URL the page was already on.
   * Read from the URL and both are gone: an expanded query carries quotes and ` OR `, expandQuery
   * hands those straight back, so `expanded === current` and the page stops. One hop, always.
   */
  #rescueEmptyResults() {
    const results = Number(this.getAttribute('data-pp-results'));
    if (!Number.isInteger(results) || results !== 0) return;

    const current = new URL(location.href).searchParams.get('q') || '';
    if (!current.trim()) return;

    const expanded = expandQuery(current, this.getAttribute('data-pp-synonyms') || '');
    if (expanded === current) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', expanded);
    /* replace, not assign: the dead-end page must not sit in history, or Back from the results
       would land the shopper straight back on "no results". */
    location.replace(url.toString());
  }
}

if (!customElements.get('pp-search-expand')) {
  customElements.define('pp-search-expand', PpSearchExpand);
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    /* On a results page this box is pre-filled with the query as run. Show the shopper their own
       words, not the machinery — this box is used to refine, and refining from a query they never
       wrote is how words go missing. */
    restoreShopperWords(this.refs.searchInput, this.dataset.ppSynonyms || '');
    this.#syncResultsId();

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          /* The dropdown and the results page ran two different searches, and the shopper saw it:
             typing "f" listed FLIPSIDE, FINN and three more, then pressing Enter reported
             "0 results for f". Predictive search matches on a prefix; the full search matches
             whole words, so a part-typed word finds nothing.

             Measured on this store rather than assumed — `options[prefix]=last`, the documented
             switch, changes nothing here (q=fli -> 0 results with and without it, also with
             type=product and options[fields]). A trailing `*` does: q=fli* -> 1 result, the same
             product the dropdown offered. So the wildcard is what actually carries the dropdown's
             behaviour across, and it goes on the LAST term only — the earlier words were typed in
             full and turning them into prefixes would widen the search past what was asked. */
          /* Vietnamese first, wildcard second — see the bridge at the top of this file. The order
             matters: "ao thun" only matches the table while it is still two plain words, and the
             wildcard then lands harmlessly on the last English synonym (measured: same 23
             products with and without it). */
          const raw = expandQuery(this.refs.searchInput.value.trim(), this.dataset.ppSynonyms || '');
          const terms = raw.split(/\s+/);
          const last = terms[terms.length - 1];
          /* Leave alone anything already carrying its own operator, or a quoted phrase — the
             shopper spelled that out deliberately. */
          if (last && !/[*"]/.test(last)) terms[terms.length - 1] = `${last}*`;

          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', terms.join(' '));
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  #syncResultsId() {
    const resultsId = this.refs.searchInput.getAttribute('aria-controls');
    const resultsListbox = this.refs.predictiveSearchResults.querySelector('[role="listbox"]');
    if (resultsId && resultsListbox) resultsListbox.id = resultsId;
    this.refs.searchInput.setAttribute(
      'aria-expanded',
      String(Boolean(this.refs.predictiveSearchResults.querySelector('[data-search-results]')))
    );
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    /* The dropdown gets the same expansion the results page gets, so the two agree: a shopper who
       sees five tees drop down while typing must not lose them by pressing Enter. Verified against
       /search/suggest.json that the endpoint accepts the quoted-phrase + OR form and returns
       products for it. Only the URL is rewritten — SearchUpdateEvent below still reports what the
       shopper actually typed, which is what any analytics app listening to it should record. */
    url.searchParams.set('q', expandQuery(searchTerm, this.dataset.ppSynonyms || ''));
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);
        this.#syncResultsId();

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#syncResultsId();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
